import React, { useEffect } from 'react';
import { useState, useMemo } from 'react';
import { FolderOpen, Calendar, FileText, Users, TrendingUp, Banknote, AlertTriangle } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { DashboardStats } from '../components/Dashboard/DashboardStats';
import { ProjectCard } from '../components/Projects/ProjectCard';
import { ProjectSelector } from '../components/Projects/ProjectSelector';
import { ProjectStatusTimeline } from '../components/Projects/ProjectStatusTimeline';
import { StatusSelector } from '../components/Projects/StatusSelector';
import { StageHistory } from '../components/Projects/StageHistory';
import { LastProjectViewer } from '../components/Dashboard/LastProjectViewer';
import { useAuthStore } from '../store/authStore';
import { useProjectStore } from '../store/projectStore';
import { useNotificationStore } from '../store/notificationStore';
import { useMessageStore } from '../store/messageStore';
import { DashboardCustomizer } from '../components/Dashboard/DashboardCustomizer';
import { supabase } from '../lib/supabase';
import { ProfileType, Project, ProjectStatus, ProjectTimeline } from '../types';
import type { Notification } from '../types/meetings';
import type { Message } from '../types/messaging';

interface RealDashboardStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  pendingDocuments: number;
  upcomingAppointments: number;
  fundingTotal: number;
  fundingRequested: number;
  documentsToValidate: number;
  recentActivity: Array<{
    project_name: string;
    status: string;
    updated_at: string;
    owner_name: string;
  }>;
}

export const DashboardPage: React.FC = () => {
  const user = useAuthStore(state => state.user);
  const projects = useProjectStore(state => state.projects);
  const lastViewedProject = useProjectStore(state => state.lastViewedProject);
  const fetchProjects = useProjectStore(state => state.fetchProjects);
  const fetchStats = useProjectStore(state => state.fetchStats);
  const updateProjectStatus = useProjectStore(state => state.updateProjectStatus);
  const fetchProjectTimeline = useProjectStore(state => state.fetchProjectTimeline);
  const notifications = useNotificationStore(state => state.notifications);
  const fetchNotifications = useNotificationStore(state => state.fetchNotifications);
  const messages = useMessageStore(state => state.messages);
  const fetchMessages = useMessageStore(state => state.fetchMessages);

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectTimeline, setProjectTimeline] = useState<ProjectTimeline | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [realStats, setRealStats] = useState<RealDashboardStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [dashboardLayout, setDashboardLayout] = useState({
    showStats: true,
    showQuickActions: true,
    showNotifications: true,
    showMessages: true,
    showProjectSelector: true
  });
  const [isTimelineFullscreen, setIsTimelineFullscreen] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchStats();
    loadRealDashboardStats();
    createWelcomeMessagesIfNeeded();
    fetchNotifications();
    fetchMessages({ unread_only: false });
  }, []);

  const createWelcomeMessagesIfNeeded = async () => {
    if (!user) return;

    try {
      // Check if user already has a welcome notification
      const { data: existingNotif } = await supabase
        .from('notifications_enhanced')
        .select('id')
        .eq('user_id', user.id)
        .eq('type', 'WELCOME')
        .maybeSingle();

      if (existingNotif) return; // Welcome already sent

      // Create welcome notification
      await supabase
        .from('notifications_enhanced')
        .insert({
          user_id: user.id,
          type: 'WELCOME',
          title: 'Bienvenue sur la plateforme CRESS',
          message: `Bienvenue ${user.full_name || 'sur la plateforme'} ! Nous sommes ravis de vous accompagner dans votre projet d'économie sociale et solidaire.`,
          is_read: false
        });

      // Find a coordinator or admin to send welcome message from
      const { data: admins } = await supabase
        .from('users')
        .select('id')
        .in('profile_type', ['COORDINATEUR', 'ADMIN'])
        .limit(1)
        .maybeSingle();

      const senderId = admins?.id || user.id; // Fallback to self if no admin found

      // Create welcome message
      await supabase
        .from('messages')
        .insert({
          sender_id: senderId,
          recipient_id: user.id,
          subject: 'Bienvenue dans l\'accompagnement ESS',
          content: `Bonjour ${user.full_name || ''},\n\nBienvenue sur la plateforme d'accompagnement de la CRESS Guadeloupe !\n\nNous sommes heureux de vous accueillir dans notre communauté de l'Économie Sociale et Solidaire.\n\nN'hésitez pas à explorer les différentes sections :\n- Créer votre projet\n- Consulter les ressources disponibles\n- Prendre rendez-vous avec un accompagnateur\n\nÀ très bientôt !\n\nL'équipe CRESS Guadeloupe`,
          is_read: false
        });

      // Refresh notifications and messages
      fetchNotifications();
      fetchMessages({ unread_only: false });
    } catch (error) {
      console.error('Erreur création messages de bienvenue:', error);
    }
  };

  const loadRealDashboardStats = async () => {
    if (!user) return;
    
    setIsLoadingStats(true);
    try {
      // Construire les requêtes selon le profil utilisateur
      let projectsQuery = supabase
        .from('projects')
        .select(`
          id, name, status, domain, funding_requested, funding_amount, updated_at,
          owner:users!projects_owner_id_fkey(full_name, email)
        `);

      // Filtrer selon le profil
      if (user.profile_type === 'PORTEUR') {
        projectsQuery = projectsQuery.eq('owner_id', user.id);
      } else if (user.profile_type === 'ACCOMPAGNATEUR') {
        projectsQuery = projectsQuery.or(`owner_id.eq.${user.id},assigned_advisor_id.eq.${user.id}`);
      }
      // COORDINATEUR, FINANCEUR, ADMIN voient tous les projets

      const { data: projectsData, error: projectsError } = await projectsQuery;
      if (projectsError) throw projectsError;

      const userProjects = projectsData || [];
      
      // Calculer les statistiques
      const totalProjects = userProjects.length;
      const activeProjects = userProjects.filter(p => 
        ['EN_COURS', 'VALIDE', 'DEBLOCAGE_FONDS', 'SOUMIS', 'ACCORD_PROVISOIRE'].includes(p.status)
      ).length;
      const completedProjects = userProjects.filter(p => p.status === 'TERMINE').length;
      const fundingTotal = userProjects.reduce((sum, p) => sum + (p.funding_amount || 0), 0);
      const fundingRequested = userProjects.reduce((sum, p) => sum + (p.funding_requested || 0), 0);

      // Documents en attente de validation (pour accompagnateurs/coordinateurs)
      let documentsToValidate = 0;
      let pendingDocuments = 0;
      
      if (['ACCOMPAGNATEUR', 'COORDINATEUR', 'ADMIN'].includes(user.profile_type || '')) {
        const projectIds = userProjects.map(p => p.id);
        if (projectIds.length > 0) {
          const { data: docsData, error: docsError } = await supabase
            .from('documents')
            .select('id, is_validated, project_id')
            .in('project_id', projectIds);

          if (!docsError && docsData) {
            documentsToValidate = docsData.filter(d => d.is_validated === null).length;
            pendingDocuments = docsData.filter(d => d.is_validated === false || d.is_validated === null).length;
          }
        }
      }

      // Rendez-vous à venir
      let upcomingAppointments = 0;
      try {
        let appointmentsQuery = supabase
          .from('appointments')
          .select('id')
          .gte('start_date', new Date().toISOString());

        if (user.profile_type === 'PORTEUR') {
          appointmentsQuery = appointmentsQuery.eq('created_by', user.id);
        } else if (user.profile_type === 'ACCOMPAGNATEUR') {
          // Rendez-vous créés par l'accompagnateur ou pour ses projets
          const projectIds = userProjects.map(p => p.id);
          if (projectIds.length > 0) {
            appointmentsQuery = appointmentsQuery.or(`created_by.eq.${user.id},project_id.in.(${projectIds.join(',')})`);
          } else {
            appointmentsQuery = appointmentsQuery.eq('created_by', user.id);
          }
        }
        // COORDINATEUR, ADMIN voient tous les RDV

        const { data: appointmentsData, error: appointmentsError } = await appointmentsQuery;
        if (!appointmentsError) {
          upcomingAppointments = appointmentsData?.length || 0;
        }
      } catch (error) {
        console.warn('Erreur chargement rendez-vous:', error);
      }

      // Activité récente
      const recentActivity = userProjects
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, 5)
        .map(p => ({
          project_name: p.name,
          status: p.status,
          updated_at: p.updated_at,
          owner_name: p.owner?.full_name || p.owner?.email || 'Porteur'
        }));

      const stats: RealDashboardStats = {
        totalProjects,
        activeProjects,
        completedProjects,
        pendingDocuments,
        upcomingAppointments,
        fundingTotal,
        fundingRequested,
        documentsToValidate,
        recentActivity
      };

      setRealStats(stats);
      console.log(`📊 Stats réelles chargées pour ${user.profile_type}:`, stats);
      
    } catch (error) {
      console.error('Erreur chargement stats dashboard:', error);
      // Fallback vers des stats vides
      setRealStats({
        totalProjects: 0,
        activeProjects: 0,
        completedProjects: 0,
        pendingDocuments: 0,
        upcomingAppointments: 0,
        fundingTotal: 0,
        fundingRequested: 0,
        documentsToValidate: 0,
        recentActivity: []
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    if (selectedProject) {
      fetchProjectTimeline(selectedProject.id).then(setProjectTimeline);
    } else {
      setProjectTimeline(null);
    }
  }, [selectedProject, fetchProjectTimeline]);

  const handleStatusUpdate = async (status: ProjectStatus, note?: string) => {
    if (!selectedProject) return;
    
    setIsUpdatingStatus(true);
    try {
      await updateProjectStatus(selectedProject.id, status, note);
      // Refresh timeline after update
      const updatedTimeline = await fetchProjectTimeline(selectedProject.id);
      setProjectTimeline(updatedTimeline);
    } catch (error) {
      console.error('Failed to update project status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatsForRole = useMemo(() => {
    if (!realStats) return [];

    switch (user?.profile_type) {
      case ProfileType.PORTEUR:
        return [
          {
            title: 'Mes projets',
            value: realStats.totalProjects,
            change: realStats.totalProjects > 0 ? { value: 12, type: 'increase' as const } : undefined,
            icon: FolderOpen,
            color: 'bg-blue-600'
          },
          {
            title: 'Projets actifs',
            value: realStats.activeProjects,
            change: realStats.activeProjects > 0 ? { value: 8, type: 'increase' as const } : undefined,
            icon: TrendingUp,
            color: 'bg-green-600'
          },
          {
            title: 'RDV à venir',
            value: realStats.upcomingAppointments,
            icon: Calendar,
            color: 'bg-purple-600'
          },
          {
            title: 'Financement demandé',
            value: realStats.fundingRequested > 0 
              ? `${(realStats.fundingRequested / 1000).toFixed(0)}K €`
              : '0 €',
            icon: Banknote,
            color: 'bg-orange-600'
          }
        ];
      
      case ProfileType.ACCOMPAGNATEUR:
        return [
          {
            title: 'Mes projets',
            value: realStats.totalProjects,
            change: realStats.totalProjects > 0 ? { value: 5, type: 'increase' as const } : undefined,
            icon: FolderOpen,
            color: 'bg-blue-600'
          },
          {
            title: 'Projets actifs',
            value: realStats.activeProjects,
            icon: TrendingUp,
            color: 'bg-green-600'
          },
          {
            title: 'RDV à venir',
            value: realStats.upcomingAppointments,
            icon: Calendar,
            color: 'bg-purple-600'
          },
          {
            title: 'Documents à valider',
            value: realStats.documentsToValidate,
            change: realStats.documentsToValidate > 0 ? { value: -3, type: 'decrease' as const } : undefined,
            icon: FileText,
            color: 'bg-orange-600'
          }
        ];
      
      case ProfileType.COORDINATEUR:
      case ProfileType.ADMIN:
      case ProfileType.FINANCEUR:
        return [
          {
            title: 'Projets actifs',
            value: realStats.totalProjects,
            change: realStats.totalProjects > 0 ? { value: 18, type: 'increase' as const } : undefined,
            icon: FolderOpen,
            color: 'bg-blue-600'
          },
          {
            title: 'Financement total',
            value: realStats.fundingTotal > 0 
              ? `${(realStats.fundingTotal / 1000000).toFixed(1)}M €`
              : '0 €',
            change: realStats.fundingTotal > 0 ? { value: 15, type: 'increase' as const } : undefined,
            icon: Banknote,
            color: 'bg-green-600'
          },
          {
            title: 'Documents en attente',
            value: realStats.documentsToValidate,
            icon: FileText,
            color: 'bg-purple-600'
          },
          {
            title: 'RDV programmés',
            value: realStats.upcomingAppointments,
            icon: Calendar,
            color: 'bg-orange-600'
          }
        ];
      
      default:
        return [
          {
            title: 'Projets',
            value: realStats.totalProjects,
            icon: FolderOpen,
            color: 'bg-blue-600'
          },
          {
            title: 'Actifs',
            value: realStats.activeProjects,
            icon: TrendingUp,
            color: 'bg-green-600'
          },
          {
            title: 'RDV',
            value: realStats.upcomingAppointments,
            icon: Calendar,
            color: 'bg-purple-600'
          },
          {
            title: 'Documents',
            value: realStats.pendingDocuments,
            icon: FileText,
            color: 'bg-orange-600'
          }
        ];
    }
  }, [realStats, user?.profile_type]);

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';
    const name = user?.full_name || user?.email?.split('@')[0] || 'Utilisateur';
    
    return `${greeting} ${name}`;
  };

  const getRecentProjects = () => {
    return projects.slice(0, 4);
  };

  const toggleTimelineFullscreen = () => {
    setIsTimelineFullscreen(!isTimelineFullscreen);
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      A_PREPARER: 'À préparer',
      SOUMIS: 'Soumis',
      ACCORD_PROVISOIRE: 'Accord provisoire',
      VALIDE: 'Validé',
      DEBLOCAGE_FONDS: 'Déblocage fonds',
      EN_COURS: 'En cours',
      VERIFICATION: 'Vérification',
      TERMINE: 'Terminé',
      REJETE: 'Rejeté',
      SUSPENDU: 'Suspendu'
    };
    return labels[status as keyof typeof labels] || status;
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 animate-fade-in">
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 animate-slide-up">
          <h1 className="text-3xl font-bold mb-2 animate-slide-down">{getWelcomeMessage()}</h1>
          <p className="text-blue-100 text-lg animate-slide-down" style={{ animationDelay: '0.1s' }}>
            Voici un aperçu de votre activité sur la plateforme CRESS
          </p>
          
          {/* Dashboard Customizer */}
          <div className="mt-4 animate-slide-down" style={{ animationDelay: '0.2s' }}>
            <DashboardCustomizer 
              layout={dashboardLayout}
              onLayoutChange={setDashboardLayout}
            />
          </div>
        </div>

        {/* Stats */}
        {dashboardLayout.showStats && (
          <div className="animate-slide-up" style={{ animationDelay: '0.1s' }}>
            {isLoadingStats ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                        <div className="h-8 bg-gray-200 rounded w-1/2" />
                      </div>
                      <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : realStats ? (
              <DashboardStats stats={getStatsForRole} />
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <p className="text-red-800">Impossible de charger les statistiques</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Last Viewed Project (Porteurs only) */}
        {user?.profile_type === ProfileType.PORTEUR && (
          <div className="animate-slide-up" style={{ animationDelay: '0.15s' }}>
            <LastProjectViewer />
          </div>
        )}

        {/* Quick Actions & Recent Projects */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          {/* Quick Actions */}
          {dashboardLayout.showQuickActions && (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-300">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h2>
            <div className="space-y-3">
              {user?.profile_type === ProfileType.PORTEUR && (
                <>
                  <button className="w-full text-left p-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-md group">
                    <div className="flex items-center space-x-3">
                      <FolderOpen className="w-5 h-5 text-blue-600 group-hover:animate-bounce-subtle" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Créer un projet</span>
                    </div>
                  </button>
                  <button className="w-full text-left p-3 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-md group">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-green-600 group-hover:animate-bounce-subtle" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Prendre RDV</span>
                    </div>
                  </button>
                </>
              )}
              {user?.profile_type === ProfileType.ACCOMPAGNATEUR && (
                <>
                  <button className="w-full text-left p-3 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/20 dark:hover:bg-purple-900/40 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-md group">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-purple-600 group-hover:animate-bounce-subtle" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Voir mon portefeuille</span>
                    </div>
                  </button>
                  <button className="w-full text-left p-3 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/40 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-md group">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-orange-600 group-hover:animate-bounce-subtle" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Planifier RDV</span>
                    </div>
                  </button>
                </>
              )}
              <button className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700/50 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-md group">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:animate-bounce-subtle" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">Consulter ressources</span>
                </div>
              </button>
            </div>
          </div>
          )}

          {/* Project Selector */}
          {dashboardLayout.showProjectSelector && (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-300">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sélectionner un projet</h2>
            <ProjectSelector
              onProjectSelect={setSelectedProject}
              selectedProject={selectedProject}
            />
          </div>
          )}
        </div>

        {/* Project Timeline Section */}
        {selectedProject && (
          <div className="space-y-6 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300">
              <h2 className="text-xl font-bold mb-2 animate-slide-down">{selectedProject.name}</h2>
              <p className="text-blue-100 animate-slide-down" style={{ animationDelay: '0.1s' }}>
                Porteur: {selectedProject.owner?.full_name || selectedProject.owner?.email}
              </p>
              {selectedProject.assigned_advisor && (
                <p className="text-blue-100 animate-slide-down" style={{ animationDelay: '0.2s' }}>
                  Accompagnateur: {selectedProject.assigned_advisor.full_name || selectedProject.assigned_advisor.email}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Timeline */}
              <div className="lg:col-span-2">
                {projectTimeline ? (
                  <ProjectStatusTimeline
                    currentStatus={projectTimeline.current_status}
                    stages={projectTimeline.stages}
                    fullscreen={isTimelineFullscreen}
                    onToggleFullscreen={toggleTimelineFullscreen}
                  />
                ) : (
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  </div>
                )}
              </div>

              {/* Status Selector */}
              <div className="space-y-6">
                {projectTimeline && (
                  <StatusSelector
                    currentStatus={projectTimeline.current_status}
                    projectId={selectedProject.id}
                    onStatusUpdate={handleStatusUpdate}
                    isLoading={isUpdatingStatus}
                  />
                )}
              </div>
            </div>

            {/* Stage History */}
            {projectTimeline && projectTimeline.stages.length > 0 && (
              <StageHistory stages={projectTimeline.stages} />
            )}
          </div>
        )}

        {/* Notifications & Messages */}
        {!selectedProject && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-slide-up" style={{ animationDelay: '0.4s' }}>
          {/* Recent Activity */}
          {realStats && realStats.recentActivity.length > 0 && (
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-300">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Activité récente</h2>
              <div className="space-y-3">
                {realStats.recentActivity.slice(0, 3).map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md group">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-200">
                      <span className="text-white text-xs font-medium">
                        {activity.owner_name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                        {activity.project_name}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {activity.owner_name} • {getStatusLabel(activity.status)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(activity.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dashboardLayout.showNotifications && (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-300">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Notifications récentes</h2>
            <div className="space-y-3">
              {notifications.length > 0 ? (
                notifications.slice(0, 3).map((notif) => {
                  const colorClass = notif.is_read ? 'bg-gray-50 dark:bg-gray-700/20' : 'bg-blue-50 dark:bg-blue-900/20';
                  const dotColor = notif.is_read ? 'bg-gray-400' : 'bg-blue-600';

                  return (
                    <div key={notif.id} className={`flex items-start space-x-3 p-3 ${colorClass} rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all duration-200 hover:scale-105 cursor-pointer group`}>
                      <div className={`w-2 h-2 ${dotColor} rounded-full mt-2`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 truncate">{notif.title}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{notif.message}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Aucune notification</p>
              )}
            </div>
          </div>
          )}

          {dashboardLayout.showMessages && (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-300">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Messages</h2>
            <div className="space-y-3">
              {messages.length > 0 ? (
                messages.slice(0, 3).map((msg) => {
                  const senderInitials = msg.sender?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
                  const timeStr = new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

                  return (
                    <div key={msg.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md group">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-200 flex-shrink-0">
                        <span className="text-white text-xs font-medium">{senderInitials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 truncate">{msg.sender?.full_name || 'Utilisateur'}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{msg.subject}</p>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 group-hover:font-medium transition-all duration-200 flex-shrink-0">{timeStr}</div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Aucun message</p>
              )}
            </div>
          </div>
          )}
        </div>
        )}
      </div>
    </Layout>
  );
};